/* ─────────────────────────────────────────────────────────────
   SelectFieldRenderer — renders a select/dropdown field.

   Supports three dataSource types:
   - static: hardcoded options array
   - context: options injected via runtimeContext[key]
   - dynamic: async options fetched from GraphQL via useDynamicOptions

   For 'dynamic' datasources, shows a loading skeleton while fetching
   and an empty state if no options are available.

   For 'context' datasources with no resolved options, shows a disabled
   select with contextual placeholder and optional emptyStateAction link.
   ───────────────────────────────────────────────────────────── */

import type { FieldRendererProps, DynamicDataSource } from '../../config/types';
import { useDynamicOptions } from '../../query/useDynamicOptions';
import { PlacesSelectRenderer } from './PlacesSelect';
import { StrictOptionSelect } from './StrictOptionSelect';
import { Combobox } from '@/components/ui/Combobox';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';

export function SelectFieldRenderer({
  field,
  value,
  onChange,
  onBlur,
  error,
  runtimeContext,
}: FieldRendererProps) {
  const isPlaces = field.dataSource?.type === 'places';
  const isDynamic = field.dataSource?.type === 'dynamic';
  const isContext = field.dataSource?.type === 'context';

  // Always call the hook unconditionally (Rules of Hooks).
  // For non-dynamic fields (incl. places) we pass a disabled stub so the hook
  // call is unconditional but fires no request.
  const dynamicDs: DynamicDataSource = isDynamic
    ? (field.dataSource as DynamicDataSource)
    : { type: 'dynamic', operation: '', variables: {}, mapResponse: { path: '', value: '', label: '' } };

  const { options: dynamicOptions, isLoading: dynamicLoading } = useDynamicOptions(
    dynamicDs,
    runtimeContext,
  );

  // Delegate to PlacesSelectRenderer for first-class places dataSource.
  // Must come AFTER all hook calls to satisfy Rules of Hooks.
  if (isPlaces) {
    return (
      <PlacesSelectRenderer
        field={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        runtimeContext={runtimeContext}
      />
    );
  }

  // A select that takes several values, or that must never fall back to free text: drawn from the
  // static options only (ENT-565, the proposal wizard's multi-selects). Opt-in by prop, so every
  // existing config renders as before.
  if (field.props?.multiple === true || field.props?.strictOptions === true) {
    return (
      <StrictOptionSelect
        field={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        runtimeContext={runtimeContext}
      />
    );
  }

  // Loading state: dynamic field awaiting async options
  if (isDynamic && dynamicLoading) {
    return <SelectFieldSkeleton id={field.id} />;
  }

  const options = isDynamic
    ? dynamicOptions
    : field.dataSource?.type === 'static'
      ? field.dataSource.options
      : isContext && field.dataSource?.type === 'context'
        ? (runtimeContext[field.dataSource.key] as { value: string; label: string }[] | undefined)
        : undefined;

  // Empty/no options state: dynamic field with no results or failed fetch
  if (isDynamic && (!options || options.length === 0)) {
    return <SelectFieldEmpty id={field.id} />;
  }

  // Context field with no resolved options — show disabled select with placeholder
  if (isContext && (!options || options.length === 0)) {
    const emptyStateAction = (field as { emptyStateAction?: { label: string; href: string } }).emptyStateAction;
    return (
      <div>
        <Combobox
          id={field.id}
          options={[]}
          placeholder={field.placeholder ?? 'Select a country first'}
          className="bg-surface-elevated"
          disabled
        />
        {emptyStateAction && (
          <a
            href={emptyStateAction.href}
            className="mt-1 text-body-sm text-primary hover:underline flex items-center gap-1"
          >
            {emptyStateAction.label} →
          </a>
        )}
      </div>
    );
  }

  /*
   * Fallback text input for non-dynamic, non-context fields that have no dataSource.
   *
   * Was a raw <input> whose className hand-copied ui/Input's — but with rounded-lg and py-2
   * where Input uses rounded and h-9. So a `select` with no dataSource rendered a visibly
   * different text box (taller, rounder) than an identical `textField` sitting next to it in the
   * same form. Input's own aria-[invalid=true]:border-danger carries the error state.
   */
  if (!options || options.length === 0) {
    return (
      <div>
        <Input
          type="text"
          id={field.id}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={field.isDisabled}
          placeholder={field.placeholder}
          aria-invalid={!!error}
        />
      </div>
    );
  }

  const selectValue = (value as string) || ''

  return (
    <div>
      <Combobox
        id={field.id}
        options={options}
        value={selectValue}
        onValueChange={(v) => onChange(v)}
        placeholder={field.placeholder ?? 'Select...'}
        disabled={field.isDisabled}
        clearable
        className={`bg-surface-elevated${error ? ' border-danger' : ''}`}
      />
    </div>
  );
}

/* ── Loading skeleton ── */

interface SkeletonProps {
  id: string;
}

function SelectFieldSkeleton({ id }: SkeletonProps) {
  return (
    <div
      id={id}
      className="h-10 w-full animate-pulse rounded-lg bg-surface-muted"
      role="status"
      aria-label="Loading options..."
    />
  );
}

/* ── Empty state ── */

interface EmptyProps {
  id: string;
}

function SelectFieldEmpty({ id }: EmptyProps) {
  return (
    <div
      id={id}
      className="flex h-10 items-center rounded-lg border border-border px-3"
      role="status"
      aria-label="No options available"
    >
      <Text variant="body-sm" color="muted">
        No options available
      </Text>
    </div>
  );
}
